import React, { Component, Children } from 'react';
import styles from './array-field.scss';

// Components
import { FormField } from '../form-field';
import Button from 'ui/button';
import { DragSource, DropTarget } from 'react-dnd';
import { ICON_CLEAR, ICON_DRAG_HANDLE } from 'icons';
import { isFormField, hasChildren } from '../utils';
import ChildField, { wantsFormData } from '../child-field';

interface ArrayItemProps {
  value: any;
  singleField: boolean;
  noValidation: boolean;
  onChange: (index: number, name: string, value: any) => void;
  onRemove: (index: number) => void;
  onAdd: (index: number) => void;
  onSort: (removeFromIndex: number, insertAtIndex: number) => void;
}
interface ArrayItemState {}

const bgSource = {
  beginDrag({ arrayId, index }) {
    return { arrayId, index };
  }
};
const bgTarget = {
  canDrop(props, monitor) {
    return props.arrayId === monitor.getItem().arrayId;
  },
  hover(props, monitor, component: ArrayItem) {
    // Determine mouse position
    const clientOffset = monitor.getClientOffset();
    component = component.getDecoratedComponentInstance();
    //  const isOverMiddle = component.getDecoratedComponentInstance().isOverMiddle(clientOffset.y);
    component.setState({
      isOverMiddle: component.isOverMiddle(clientOffset.y)
    });
  },

  drop(props, monitor, component: ArrayItem) {
    const dragIndex = monitor.getItem().index;
    const hoverIndex = props.index;

    // Don't replace items with themselves
    if (dragIndex === hoverIndex) return;
    // Determine mouse position
    const clientOffset = monitor.getClientOffset();
    const isOverMiddle = component.getDecoratedComponentInstance().isOverMiddle(clientOffset.y);

    // Dragging downwards
    if (dragIndex < hoverIndex && !isOverMiddle) return;

    // Dragging upwards
    if (dragIndex > hoverIndex && isOverMiddle) return;
    monitor.getItem().index = hoverIndex;
    props.onSort(dragIndex, hoverIndex);
  }
};

let id = 0;

@DropTarget('array-item', bgTarget, (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver() && monitor.canDrop()
}))
@DragSource('array-item', bgSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
  isDragging: monitor.isDragging()
}))
export default class ArrayItem extends Component<ArrayItemProps, ArrayItemState> {
  state = {
    isOverMiddle: false
  };

  fields: { [string]: FormField } = {};
  field: ?FormField;
  id = 'array-item-' + id++;

  validate(): boolean {
    if (this.props.singleField) {
      return this.field.validate();
    }
    let valid = true;
    for (let fieldKey in this.fields) {
      valid &= this.fields[fieldKey].validate();
    }
    return valid;
  }

  getErrors(): string | false {
    if (this.props.singleField) {
      const error = this.field.getErrors();
      if (error) return error;
    }
    for (let fieldKey in this.fields) {
      const error = this.fields[fieldKey].getErrors();
      if (error) return error;
    }
    return false;
  }

  isOverMiddle(pageY: number): boolean {
    let rect = this.container.getBoundingClientRect();
    return pageY > rect.top + rect.height / 2;
  }

  handleChange = (name, value) => {
    this.props.onChange(this.props.index, name, value);
  };

  handleRemove = () => {
    this.props.onRemove(this.props.index);
  };

  handleAdd = () => {
    this.props.onAdd(this.props.index);
  };

  handleRef = (name, ref) => {
    if (this.props.singleField) this.field = ref;
    else {
      // If filed uses storeConnect we need to get ref.ref
      if (ref) this.fields[name] = ref.ref || ref;
      else delete this.fields[name];
    }
  };

  renderChild = (child: React.ReactNode) => {
    const { value, singleField, tiny } = this.props;

    if (isFormField(child)) {
      if (!singleField && !child.props.name) {
        return (
          <div className="text-error">
            {child.type.displayName || 'Field'} is missing name attribute
          </div>
        );
      }

      return (
        <ChildField
          child={child}
          childRef={this.handleRef}
          formData={this.props.formData}
          name={child.props.name}
          noValidation={this.props.noValidation}
          onChange={this.handleChange}
          tiny={tiny}
          value={singleField ? value : value && value[child.props.name]}
        />
      );
    } else if (wantsFormData(child)) {
      return (
        <child.type {...child.props} formData={this.props.formData}>
          {Children.map(child.props.children, this.renderChild)}
        </child.type>
      );
    } else if (hasChildren(child)) {
      return (
        <child.type {...child.props}>
          {Children.map(child.props.children, this.renderChild)}
        </child.type>
      );
    }
    return child;
  };

  render() {
    const {
      carousel,
      children,
      connectDragPreview,
      connectDragSource,
      connectDropTarget,
      isDragging,
      isOver,
      singleField
    } = this.props;

    let cl = [styles.item];
    if (isDragging) cl.push(styles.isDragging);
    else if (isOver) {
      if (this.state.isOverMiddle) cl.push(styles.isOverBottom);
      else cl.push(styles.isOverTop);
    }

    const firstButtonStyle = singleField ? { marginRight: 5 } : { marginBottom: 5 };

    return (
      <div className={styles.itemHolder + (singleField ? ' ' + styles.singleField : '')}>
        {!carousel && <div className={styles.itemMarker} />}
        {connectDragPreview(
          connectDropTarget(
            <div
              className={cl.join(' ')}
              ref={ref => (this.container = ref)}
              style={{ paddingLeft: carousel ? 0 : 10 }}
            >
              {Children.map(children, this.renderChild)}
            </div>
          )
        )}
        {!carousel && (
          <div className={styles.btns}>
            {connectDragSource(
              <div>
                <Button icon={ICON_DRAG_HANDLE} circle tabIndex={-1} />
              </div>
            )}
            <div>
              <Button icon={ICON_CLEAR} circle onClick={this.handleRemove} tabIndex={-1} />
            </div>
          </div>
        )}
      </div>
    );
  }
}
